import type { StyleSpecification } from "maplibre-gl";

// Shared basemap style/theme config — pulled out of LiveMap.tsx (2026-09-23)
// so a second, smaller map (NodeDetailPage's position-history track) can
// reuse the exact same tile source and theme handling instead of
// reimplementing it. See the original comment in LiveMap.tsx (still there,
// slightly trimmed) for why OSM raster tiles rather than CARTO, and why the
// dark "variant" is a raster-paint adjustment rather than a second tile
// source.

// Basemap, no API key. CARTO's free Positron/Dark Matter raster tiles now
// require a registered key (they still 200 but silently serve an "API key
// required" watermark tile), so plain OpenStreetMap standard tiles are the
// genuinely-keyless choice. There's no equally-established keyless *dark*
// raster tile source, so the dark variant darkens/desaturates these same
// OSM tiles via MapLibre's own raster-* paint properties on just this one
// layer, rather than loading a second tile source or CSS-filtering the
// whole canvas (which would also invert any node/marker colours drawn on
// top).
export const RASTER_PAINT: Record<"light" | "dark", { brightnessMax: number; saturation: number; contrast: number }> = {
  light: { brightnessMax: 1, saturation: -0.15, contrast: 0 },
  dark: { brightnessMax: 0.18, saturation: -0.6, contrast: 0.3 },
};

// The map's own "settled" background tone per theme — close to the actual
// average rendered tile colour once RASTER_PAINT has been applied. Used as
// the map CONTAINER's own CSS background so there's no contrasting flash
// while raster tiles are still loading, before any pixel has painted.
export const MAP_CONTAINER_BACKGROUND: Record<"light" | "dark", string> = {
  light: "#e7e5dd",
  dark: "#333333",
};

// Built fresh at map-construction time with the caller's already-resolved
// theme baked directly into the initial style, so the very first frame is
// already right (no flash from a theme-blind default later corrected by a
// setPaintProperty effect).
export function buildOsmStyle(theme: "light" | "dark"): StyleSpecification {
  const p = RASTER_PAINT[theme];
  return {
    version: 8,
    sources: {
      osm: {
        type: "raster",
        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "&copy; OpenStreetMap contributors",
      },
    },
    layers: [
      {
        id: "osm-tiles",
        type: "raster",
        source: "osm",
        paint: { "raster-saturation": p.saturation, "raster-brightness-max": p.brightnessMax, "raster-contrast": p.contrast },
      },
    ],
  };
}

// A dedicated, always-light raster-paint profile for the Backbone tab's
// map (2026-09-30) -- its own repeater-class colours (orange/purple/red)
// and dark pill text need a quiet, low-contrast MeshMapper-like backdrop
// regardless of the app's own dark-mode toggle (the approved prototype
// had no dark mode at all). This is NOT a second tile source or a vector
// style: the module comment above already found CARTO's free Positron/
// Dark-Matter raster tiles now require a registered API key (silent
// watermark tile otherwise), so there's no genuinely keyless alternative
// to plain OSM raster tiles here either -- this profile just pushes the
// SAME keyless OSM source much further toward flat/desaturated than
// RASTER_PAINT.light does, close to a Positron look, without adding a
// new dependency or tile source. `raster-brightness-max` is capped at 1
// by MapLibre's own style spec (a value above 1 throws and silently
// breaks the WHOLE style load, which is a real bug hit here). A real
// browser check also found `brightness-min` much above 0 combined with
// negative contrast flattens ocean tiles (already near-uniform colour)
// into a stark, textureless grey rectangle that reads as a broken/
// missing tile -- so brightness is left at its default floor and only
// saturation does the "flattening" work, with contrast at 0.
const BACKBONE_RASTER_PAINT = { brightnessMin: 0, brightnessMax: 1, saturation: -0.75, contrast: 0 };

export function buildBackboneMapStyle(): StyleSpecification {
  const p = BACKBONE_RASTER_PAINT;
  return {
    version: 8,
    sources: {
      osm: {
        type: "raster",
        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "&copy; OpenStreetMap contributors",
      },
    },
    layers: [
      {
        id: "osm-tiles",
        type: "raster",
        source: "osm",
        paint: {
          "raster-saturation": p.saturation,
          "raster-brightness-min": p.brightnessMin,
          "raster-brightness-max": p.brightnessMax,
          "raster-contrast": p.contrast,
        },
      },
    ],
  };
}
